/**
 * F (2026-05-25) - Compliance Posture Federation Dashboard.
 *
 * THE demo screen for the customer CISO. Shows all 7 federation entity
 * counts in one view:
 *   1. Data Connections (DPDPA §10 data fiduciary inventory)
 *   2. DSARs (DPDPA §11-§14 + GDPR Art 12-22)
 *   3. Erasures (DPDPA §12 + GDPR Art 17)
 *   4. Breaches (DPDPA §10 + GDPR Art 33-34)
 *   5. Vendors (cross-border DPA tracking)
 *   6. Risks (DPDPA + SEBI CSCRF Control 35)
 *   7. Training (SEBI CSCRF Control 22 + ISO 27001)
 *
 * Pure read-side dashboard. Counts come from Discovery's mirror
 * tables - which populate as Core dispatches federated events.
 * Empty-state UX when mirrors are empty (Core not yet emitting,
 * or just provisioned).
 *
 * Tone:
 *   - red banner when authority deadlines breached / past SLA / criticals open
 *   - amber for "needs attention" (DPAs expiring, training overdue, errors)
 *   - green when posture is clean
 */
import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  Database,
  FileWarning,
  GraduationCap,
  Inbox,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  TrendingDown,
  Users,
} from 'lucide-react';
import {
  GlassCard,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';
import {
  getCompliancePostureFederation,
  type ComplianceFederationResponse,
} from '@/lib/api';

// ─── Card configuration table ──────────────────────────────────────

interface CardSpec {
  id: keyof Omit<ComplianceFederationResponse, 'coreIntegrated'>;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  regulationCite: string;
}

const CARDS: ReadonlyArray<CardSpec> = [
  {
    id: 'dataConnections',
    title: 'Data Connections',
    subtitle: 'Registered customer databases',
    icon: <Database className="h-5 w-5 text-blue-400" />,
    regulationCite: 'DPDPA §10 data fiduciary inventory',
  },
  {
    id: 'dsars',
    title: 'Data Subject Requests',
    subtitle: 'Access / erasure / portability requests',
    icon: <Inbox className="h-5 w-5 text-purple-400" />,
    regulationCite: 'DPDPA §11-§14 · GDPR Art 12-22',
  },
  {
    id: 'erasures',
    title: 'Right-to-Erasure Jobs',
    subtitle: 'Forgotten-by-design execution',
    icon: <Trash2 className="h-5 w-5 text-pink-400" />,
    regulationCite: 'DPDPA §12 · GDPR Art 17',
  },
  {
    id: 'breaches',
    title: 'Breach Incidents',
    subtitle: 'Lifecycle + authority notification',
    icon: <ShieldAlert className="h-5 w-5 text-red-400" />,
    regulationCite: 'DPDPA §10 · GDPR Art 33-34',
  },
  {
    id: 'vendors',
    title: 'Vendor DPA Tracking',
    subtitle: 'Cross-border DPA status',
    icon: <Users className="h-5 w-5 text-amber-400" />,
    regulationCite: 'DPDPA §16 · SEBI Master Direction',
  },
  {
    id: 'risks',
    title: 'Risk Register',
    subtitle: 'Identified + signed-off cyber risks',
    icon: <FileWarning className="h-5 w-5 text-orange-400" />,
    regulationCite: 'DPDPA · SEBI CSCRF Control 35',
  },
  {
    id: 'training',
    title: 'Compliance Training',
    subtitle: 'Awareness training coverage',
    icon: <GraduationCap className="h-5 w-5 text-green-400" />,
    regulationCite: 'SEBI CSCRF Control 22 · ISO 27001',
  },
];

// ─── Tone helpers ──────────────────────────────────────────────────

type Tone = 'critical' | 'warning' | 'info' | 'success';

function toneForCard(
  cardId: CardSpec['id'],
  data: ComplianceFederationResponse,
): Tone {
  switch (cardId) {
    case 'dataConnections':
      if (data.dataConnections.error > 0) return 'warning';
      return data.dataConnections.total === 0 ? 'info' : 'success';
    case 'dsars':
      if (data.dsars.pastSla > 0) return 'critical';
      if (data.dsars.open > 0) return 'warning';
      return data.dsars.total === 0 ? 'info' : 'success';
    case 'erasures':
      if (data.erasures.blocked > 0) return 'critical';
      if (data.erasures.partial > 0 || data.erasures.active > 0) return 'warning';
      return data.erasures.total === 0 ? 'info' : 'success';
    case 'breaches':
      if (data.breaches.pastAuthorityDeadline > 0) return 'critical';
      if (data.breaches.involvingMinors > 0) return 'critical';
      if (data.breaches.detected > 0) return 'warning';
      return data.breaches.total === 0 ? 'info' : 'success';
    case 'vendors':
      if (data.vendors.expired > 0) return 'critical';
      if (data.vendors.expiringSoon > 0) return 'warning';
      return data.vendors.total === 0 ? 'info' : 'success';
    case 'risks':
      if (data.risks.criticalSeverity > 0) return 'critical';
      if (data.risks.highSeverity > 0) return 'warning';
      return data.risks.total === 0 ? 'info' : 'success';
    case 'training':
      if (data.training.overdue > 0) return 'critical';
      if (data.training.complianceRate < 80 && data.training.total > 0) return 'warning';
      return data.training.total === 0 ? 'info' : 'success';
  }
}

function toneRing(t: Tone): string {
  switch (t) {
    case 'critical':
      return 'ring-1 ring-red-500/40 bg-red-500/5';
    case 'warning':
      return 'ring-1 ring-amber-500/40 bg-amber-500/5';
    case 'success':
      return 'ring-1 ring-emerald-500/30 bg-emerald-500/5';
    case 'info':
    default:
      return 'ring-1 ring-slate-500/20 bg-slate-500/5';
  }
}

function toneBadge(t: Tone): 'critical' | 'warning' | 'info' | 'success' {
  return t;
}

// ─── Card body renderer per entity ─────────────────────────────────

function renderCardMetrics(
  cardId: CardSpec['id'],
  data: ComplianceFederationResponse,
): React.ReactNode {
  switch (cardId) {
    case 'dataConnections': {
      const c = data.dataConnections;
      return (
        <>
          <Metric label="Total" value={c.total} hero />
          <Metric label="Connected" value={c.connected} tone="success" />
          <Metric label="Error" value={c.error} tone={c.error > 0 ? 'critical' : 'success'} />
          <Metric label="Pending" value={c.pending} />
        </>
      );
    }
    case 'dsars': {
      const c = data.dsars;
      return (
        <>
          <Metric label="Open" value={c.open} hero tone={c.pastSla > 0 ? 'critical' : 'warning'} />
          <Metric label="Past SLA" value={c.pastSla} tone={c.pastSla > 0 ? 'critical' : 'success'} />
          <Metric label="Closed" value={c.closed} tone="success" />
          <Metric label="Rejected" value={c.rejected} />
        </>
      );
    }
    case 'erasures': {
      const c = data.erasures;
      return (
        <>
          <Metric label="Active" value={c.active} hero tone={c.blocked > 0 ? 'critical' : 'warning'} />
          <Metric label="Blocked" value={c.blocked} tone={c.blocked > 0 ? 'critical' : 'success'} />
          <Metric label="Completed" value={c.completed} tone="success" />
          <Metric label="Cert Issued" value={c.certIssued} tone="success" />
        </>
      );
    }
    case 'breaches': {
      const c = data.breaches;
      return (
        <>
          <Metric
            label="Open"
            value={c.detected + c.contained}
            hero
            tone={c.pastAuthorityDeadline > 0 ? 'critical' : c.detected > 0 ? 'warning' : 'success'}
          />
          <Metric
            label="Past 72h"
            value={c.pastAuthorityDeadline}
            tone={c.pastAuthorityDeadline > 0 ? 'critical' : 'success'}
          />
          <Metric
            label="Minor Data"
            value={c.involvingMinors}
            tone={c.involvingMinors > 0 ? 'critical' : 'success'}
          />
          <Metric label="Authority Notified" value={c.authorityNotified} />
        </>
      );
    }
    case 'vendors': {
      const c = data.vendors;
      return (
        <>
          <Metric label="Total" value={c.total} hero />
          <Metric label="DPA Signed" value={c.signed} tone="success" />
          <Metric
            label="Expiring Soon"
            value={c.expiringSoon}
            tone={c.expiringSoon > 0 ? 'warning' : 'success'}
          />
          <Metric label="Expired" value={c.expired} tone={c.expired > 0 ? 'critical' : 'success'} />
        </>
      );
    }
    case 'risks': {
      const c = data.risks;
      return (
        <>
          <Metric
            label="Open"
            value={c.open}
            hero
            tone={c.criticalSeverity > 0 ? 'critical' : c.highSeverity > 0 ? 'warning' : 'success'}
          />
          <Metric
            label="Critical"
            value={c.criticalSeverity}
            tone={c.criticalSeverity > 0 ? 'critical' : 'success'}
          />
          <Metric
            label="High"
            value={c.highSeverity}
            tone={c.highSeverity > 0 ? 'warning' : 'success'}
          />
          <Metric label="Closed" value={c.closed} tone="success" />
        </>
      );
    }
    case 'training': {
      const c = data.training;
      return (
        <>
          <Metric
            label="Compliance"
            value={`${c.complianceRate}%`}
            hero
            tone={c.complianceRate < 80 ? 'warning' : 'success'}
          />
          <Metric label="Completed" value={c.completed} tone="success" />
          <Metric label="Assigned" value={c.assigned} />
          <Metric
            label="Overdue"
            value={c.overdue}
            tone={c.overdue > 0 ? 'critical' : 'success'}
          />
        </>
      );
    }
  }
}

function Metric({
  label,
  value,
  tone,
  hero,
}: {
  label: string;
  value: number | string;
  tone?: Tone;
  hero?: boolean;
}) {
  const valueClass = hero
    ? 'text-3xl font-semibold tabular-nums'
    : 'text-xl font-medium tabular-nums';
  const toneClass =
    tone === 'critical'
      ? 'text-red-300'
      : tone === 'warning'
        ? 'text-amber-300'
        : tone === 'success'
          ? 'text-emerald-300'
          : 'text-slate-100';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`${valueClass} ${toneClass}`}>{value}</span>
    </div>
  );
}

// ─── Overall posture summary banner ────────────────────────────────

function overallToneFromCards(data: ComplianceFederationResponse): {
  tone: Tone;
  headline: string;
  detail: string;
} {
  const criticalCount = CARDS.filter((c) => toneForCard(c.id, data) === 'critical').length;
  const warningCount = CARDS.filter((c) => toneForCard(c.id, data) === 'warning').length;
  if (criticalCount > 0) {
    return {
      tone: 'critical',
      headline: `${criticalCount} compliance area${criticalCount === 1 ? '' : 's'} need immediate action`,
      detail:
        'One or more federation entities have breached statutory deadlines or critical-severity findings. Open the affected card for context.',
    };
  }
  if (warningCount > 0) {
    return {
      tone: 'warning',
      headline: `${warningCount} compliance area${warningCount === 1 ? '' : 's'} need attention`,
      detail: 'No statutory breach yet - but trends indicate intervention is warranted.',
    };
  }
  const totalRows = CARDS.reduce(
    (s, c) => s + Number((data[c.id] as { total?: number } | undefined)?.total ?? 0),
    0,
  );
  if (totalRows === 0) {
    return {
      tone: 'info',
      headline: 'No federation data yet',
      detail:
        'FOCTTA Core has not yet dispatched any events to this tenant. The dashboard will populate live as customers exercise data rights, vendors are registered, risks are filed, etc.',
    };
  }
  return {
    tone: 'success',
    headline: 'Compliance posture is healthy',
    detail:
      'All federation entities show green. No breached statutory deadlines, no expired DPAs, no critical-severity risks open, training coverage is on-track.',
  };
}

// ─── Page ──────────────────────────────────────────────────────────

export function CompliancePostureFederation() {
  const [data, setData] = useState<ComplianceFederationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCompliancePostureFederation()
      .then((r) => {
        if (!cancelled) {
          setData(r);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'unknown error');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  return (
    <PageContainer>
      <PageHeader
        title="Compliance Posture - Federation Dashboard"
        subtitle="Unified compliance picture across FOCTTA Core's authoritative registers - data connections, data subject rights, erasure jobs, breaches, vendors, risks, training. Counts update in real time as Core dispatches events."
        badges={[{ label: 'All 7 federation entities', variant: 'info' }]}
        actions={
          <>
            <FocttaCoreBadge />
            <Button
              variant="glass"
              size="sm"
              onClick={() => setRefreshTick((t) => t + 1)}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="ml-2">Refresh</span>
            </Button>
          </>
        }
      />

      {error && (
        <GlassCard className="border-red-500/30 bg-red-500/5">
          <CardHeader>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
              <div>
                <CardTitle className="text-red-300">Failed to load federation data</CardTitle>
                <CardDescription className="mt-1">{error}</CardDescription>
              </div>
            </div>
          </CardHeader>
        </GlassCard>
      )}

      {loading && !data && (
        <GlassCard>
          <CardContent className="py-12 flex flex-col items-center text-slate-400">
            <RefreshCw className="h-6 w-6 animate-spin mb-3" />
            <span className="text-sm">Loading federation counts…</span>
          </CardContent>
        </GlassCard>
      )}

      {data && (
        <>
          {/* Overall posture banner */}
          {(() => {
            const o = overallToneFromCards(data);
            const Icon =
              o.tone === 'critical'
                ? ShieldAlert
                : o.tone === 'warning'
                  ? Bell
                  : o.tone === 'success'
                    ? ShieldCheck
                    : Shield;
            const colorClass =
              o.tone === 'critical'
                ? 'border-red-500/40 bg-red-500/8'
                : o.tone === 'warning'
                  ? 'border-amber-500/40 bg-amber-500/8'
                  : o.tone === 'success'
                    ? 'border-emerald-500/30 bg-emerald-500/5'
                    : 'border-slate-500/20 bg-slate-500/5';
            const iconColorClass =
              o.tone === 'critical'
                ? 'text-red-400'
                : o.tone === 'warning'
                  ? 'text-amber-400'
                  : o.tone === 'success'
                    ? 'text-emerald-400'
                    : 'text-slate-400';
            return (
              <GlassCard className={`${colorClass} mb-4`}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <Icon className={`h-6 w-6 mt-0.5 shrink-0 ${iconColorClass}`} />
                    <div className="flex-1">
                      <CardTitle>{o.headline}</CardTitle>
                      <CardDescription className="mt-1">{o.detail}</CardDescription>
                    </div>
                    <Badge variant={toneBadge(o.tone)}>{o.tone.toUpperCase()}</Badge>
                  </div>
                </CardHeader>
              </GlassCard>
            );
          })()}

          {/* Empty-state nudge when Core isn't integrated */}
          {!data.coreIntegrated && (
            <GlassCard className="border-slate-500/30 bg-slate-500/5 mb-4">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <TrendingDown className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <CardTitle>FOCTTA Core not connected</CardTitle>
                    <CardDescription className="mt-1">
                      The federation dashboard is most powerful when FOCTTA Core is paired -
                      that's when DSARs, vendor agreements, risks, and training assignments flow
                      automatically. Connect at{' '}
                      <code className="font-mono">/admin/integrations/foctta-core</code>.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </GlassCard>
          )}

          {/* Entity grid - 2 columns on lg, 1 on smaller */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {CARDS.map((card) => {
              const tone = toneForCard(card.id, data);
              const total = Number((data[card.id] as { total?: number } | undefined)?.total ?? 0);
              return (
                <GlassCard
                  key={card.id}
                  className={`${toneRing(tone)} transition`}
                  data-testid={`federation-card-${card.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      {card.icon}
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {card.title}
                          {total === 0 && (
                            <Badge variant="glass" className="text-[10px]">
                              empty
                            </Badge>
                          )}
                          {tone === 'critical' && total > 0 && (
                            <Badge variant="critical" className="text-[10px]">
                              action needed
                            </Badge>
                          )}
                          {tone === 'success' && total > 0 && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          )}
                        </CardTitle>
                        <CardDescription className="mt-0.5">
                          {card.subtitle}
                          <span className="ml-2 text-[11px] text-slate-500">
                            · {card.regulationCite}
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6">
                      {renderCardMetrics(card.id, data)}
                    </div>
                  </CardContent>
                </GlassCard>
              );
            })}
          </div>
        </>
      )}
    </PageContainer>
  );
}

export default CompliancePostureFederation;
